import * as APIService from "../api/index.js";
import URL from "../api/ConstantURL";
import { getDefaultSessionRoute, getStoredUserToken, USER_ROLE_CAREGIVER, USER_ROLE_FAMILY } from "./sessionContext";
import { setManyToLocalStorage, setToLocalStorage } from "./localStorage";
import { clearContactVerificationPromptSession } from "./contactVerification";
import { getFamilyMemberFullName } from "../api/loginApi";
import { clearAllForgotPasswordValidatedRoles } from "../api/forgotPasswordApi";
import { clearFamilyClientsCache, getClientsByFamilyMember } from "../api/familyportalOtpApi";
import { clearAllOtpResendLocks } from "./forgotPasswordResendLock";
import { clearFamilyPortalConsentCache, markFamilyPortalConsentConfirmed } from "./familyPortalConsent";

const DEVICE_PERSISTENT_KEYS = ["appVersion"];

export const persistFamilyClientSelection = (client) => {
  if (!client?.clientId) {
    return;
  }

  setManyToLocalStorage({
    familyMemberClientId: client.clientId,
    familyClientId: client.clientId,
    familyClientName: client.clientName || "",
  });
};

let ongoingLogoutPromise = null;

const readPersistentLocalValues = () => {
  const preserved = {};

  DEVICE_PERSISTENT_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      preserved[key] = value;
    }
  });

  return preserved;
};

const restorePersistentLocalValues = (preserved = {}) => {
  Object.entries(preserved).forEach(([key, value]) => {
    setToLocalStorage(key, value);
  });
};

export const clearInMemoryUserCaches = () => {
  clearFamilyClientsCache();
  clearAllForgotPasswordValidatedRoles();
  clearFamilyPortalConsentCache();
};

/** Clears the active user session so another account can use the same tab safely. */
export const clearUserSessionState = () => {
  const preserved = readPersistentLocalValues();
  localStorage.clear();
  sessionStorage.clear();
  restorePersistentLocalValues(preserved);
  clearInMemoryUserCaches();
  clearAllOtpResendLocks();
  clearContactVerificationPromptSession();
};

export const clearSessionPreservingAppVersion = () => {
  clearUserSessionState();
};

export const resolveSessionLandingRoute = () => {
  return getDefaultSessionRoute();
};

const persistFamilyMemberSession = (loginRes, email = "") => {
  const { familyMemberInfo = {} } = loginRes;
  const fullName = getFamilyMemberFullName(familyMemberInfo);
  const familyMemberId = familyMemberInfo.familyMemberId || "";
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();

  setManyToLocalStorage({
    user_token: loginRes.token,
    userRole: USER_ROLE_FAMILY,
    familyMemberName: fullName,
    ...(normalizedEmail && { userEmail: normalizedEmail }),
    ...(familyMemberId && { familyMemberId }),
  });
};

const persistCaregiverSession = (loginRes, email = "") => {
  const { caregiverInfo = {} } = loginRes;
  const caregiverId = caregiverInfo.caregiverId;
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();

  if (!caregiverId) {
    return { success: false, message: "Invalid caregiver login response." };
  }

  setManyToLocalStorage({
    caregiverId,
    caregiverName: caregiverInfo.caregiverName,
    user_token: loginRes.token,
    userRole: USER_ROLE_CAREGIVER,
    ...(normalizedEmail && { userEmail: normalizedEmail }),
  });

  return { success: true, caregiverId };
};

const applyFamilyClientSelection = async (familyMemberId) => {
  if (!familyMemberId) {
    return { path: "/family-select-client" };
  }

  try {
    const result = await getClientsByFamilyMember(familyMemberId);

    if (!result.success || !Array.isArray(result.data)) {
      return { path: "/family-select-client" };
    }

    const clients = result.data;

    // Always default-select the first mapped client and go home.
    // Multi-client switching happens from Family Home, not a select screen.
    if (clients.length >= 1) {
      persistFamilyClientSelection(clients[0]);
      return { path: "/family-home", state: { clients } };
    }

    return { path: "/family-select-client" };
  } catch {
    return { path: "/family-select-client" };
  }
};

const applyCaregiverCommunitySelection = async (caregiverId) => {
  try {
    const communityRes = await APIService.getData(`${URL.getCommunityListForCaregiver}?caregiverId=${caregiverId}`);

    if (communityRes.status !== "success") {
      return { path: "/select-community" };
    }

    const communities = communityRes.data || [];

    if (communities.length === 1) {
      const { id, communityName } = communities[0];
      setManyToLocalStorage({
        communityFlag: 0,
        communityId: id,
        communityName,
      });
      return { path: getDefaultSessionRoute() };
    }

    if (communities.length > 1) {
      setToLocalStorage("communityFlag", 1);
      return {
        path: "/select-community",
        state: { communities },
      };
    }

    setToLocalStorage("communityFlag", 0);
    return { path: getDefaultSessionRoute() };
  } catch {
    return { path: "/select-community" };
  }
};

export const resolvePostLoginNavigation = async (loginRes, email) => {
  clearUserSessionState();

  if (loginRes.role === "family") {
    persistFamilyMemberSession(loginRes, email);

    // Agreement not accepted yet: load no client data now. FamilyConsentGate shows
    // the agreement on /family-select-client, which fetches clients once accepted.
    if (loginRes.familyMemberInfo?.consentAccepted !== true) {
      return { success: true, path: "/family-select-client" };
    }

    markFamilyPortalConsentConfirmed();
    const familyMemberId = loginRes.familyMemberInfo?.familyMemberId || "";
    const navigation = await applyFamilyClientSelection(familyMemberId);
    return {
      success: true,
      path: navigation.path,
      ...(navigation.state ? { state: navigation.state } : {}),
    };
  }

  if (loginRes.role === "caregiver") {
    const persistResult = persistCaregiverSession(loginRes, email);
    if (!persistResult.success) {
      return persistResult;
    }

    const navigation = await applyCaregiverCommunitySelection(persistResult.caregiverId);
    return {
      success: true,
      path: navigation.path,
      ...(navigation.state ? { state: navigation.state } : {}),
    };
  }

  return {
    success: false,
    message: "Unsupported user role.",
  };
};

export const notifyNativeAccessToken = async (token) => {
  if (!token || !window.flutter_inappwebview?.callHandler) {
    return;
  }

  try {
    await window.flutter_inappwebview.callHandler("tokenHandler", token);
  } catch (error) {
    console.error("Flutter token handler error:", error);
  }
};

export const performSessionLogout = async ({ navigate, redirectPath = "/login", invalidateServerSession = true, notifyNative = true } = {}) => {
  if (ongoingLogoutPromise) {
    return ongoingLogoutPromise;
  }

  ongoingLogoutPromise = (async () => {
    if (notifyNative && window.flutter_inappwebview?.callHandler) {
      try {
        await window.flutter_inappwebview.callHandler("logout");
      } catch (error) {
        console.error("Flutter logout handler error:", error);
      }
    }

    if (invalidateServerSession && getStoredUserToken()) {
      try {
        await APIService.getDataAPI(URL.caregiverLogout);
      } catch (error) {
        console.error("Logout API error:", error);
      }
    }

    clearUserSessionState();

    if (typeof navigate === "function") {
      navigate(redirectPath, { replace: true });
    }
  })();

  try {
    await ongoingLogoutPromise;
  } finally {
    ongoingLogoutPromise = null;
  }
};
